import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { AlertOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { useToggle } from 'ahooks';
import { message, Modal, Upload } from 'antd';
import { UploadChangeParam, UploadProps } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { uploadNotify } from 'services/api/oms';
import { useAuthorization } from 'components/Authorization';
import { ImageInfo } from 'components/TemplateEditor/types';
import ToolbarStickerPopover from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ToolbarStickerPopover';
import useMustacheSticker2Url from 'hooks/business/useMustacheSticker2Url';
import validatorVideo, { getVideoThumbUrl } from 'utils/validator/video';
import { platformUploadImageAcceptMap, platformUploadSizeLimitMap } from 'services/config/platform';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { noop, uniq } from 'lodash-es';
import { isAeProduct } from 'productLine';
import ProxyVideo from 'components/ProxyVideo';
import { validatorLazadaImage } from 'utils/validator/lazadaImage';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

const styles = {
  container: css`
    margin-left: 8px;
    .ant-upload-list-picture-card {
      .ant-upload-select-picture-card {
        height: 72px !important;
        width: 72px !important;
      }
      .ant-upload-list-picture-card-container {
        height: 72px !important;
        width: 72px !important;
        .ant-upload-list-item-uploading {
          font-size: 12px;
          display: flex;
          flex-direction: column;
        }
      }
      .ant-upload-list-item-name {
        display: none;
      }
      video {
        width: 100%;
      }
    }
  `,
  uploadButton: css`
    margin-top: 8px;
  `,
  imageTip: css`
    font-size: 10px;
    padding-left: 12px;
    color: #84868c;
  `,
  redText: css`
    color: red;
    margin: 0 4px;
  `,
};

export const WrapStyle = css`
  border: solid 1px #f0f2f5;
  border-radius: 2px;
  padding: 12px 4px 6px;
  margin-bottom: 20px;
`;

interface ImageUploadResponse {
  data: {
    localUrl?: string;
    url: string;
    id: number;
    platformUrl?: string;
    durationSeconds?: number;
    thumbUrl?: string;
  };
}

interface Props extends UploadProps {
  fileSource: ImageInfo[];
  onImagesChange: (images: ImageInfo[]) => void;
  fileType: 'image' | 'video' | 'sticker' | 'follow' | 'qrcode';
  platform?: General.PlatformUpperCase;
  region: General.CaselessRegion;
  uploadStatusNotify?: (loadStaus: boolean) => void;
  addable?: boolean;
  isQa?: boolean;
}

export function getStickerImageId(pid: string, sid: string) {
  return `${pid}@${sid}`;
}

const FileUpload = (props: Props) => {
  const {
    className,
    style,
    maxCount,
    disabled,
    fileSource,
    onImagesChange,
    platform,
    region,
    fileType,
    uploadStatusNotify,
    addable = true,
    isQa = false,
    ...otherProps
  } = props;

  const { token } = useAuthorization();

  const { t } = useTranslate();

  const [fileList, setFileList] = useState<UploadFile<ImageUploadResponse>[]>(() =>
    fileSource.map(
      ({ imageId, size, status, error, originFileObj, name, thumbUrl, ...others }, index) => {
        if (error && status === 'error') {
          if (error.status === 401) {
            // eslint-disable-next-line no-param-reassign
            error.message = t('upload.authorizeFailed', {
              name: originFileObj.name ?? '',
            });
          } else {
            // eslint-disable-next-line no-param-reassign
            error.message = t('upload.uploadFailed', {
              name: originFileObj.name ?? '',
            });
          }
        }

        const defaultName = name || `${imageId}#${index}`;
        return {
          ...others,
          type: fileType,
          uid: `${imageId}#${index}`,
          name: status === 'error' ? t('upload.failed') : defaultName,
          url: imageId,
          thumbUrl: thumbUrl || imageId,
          size: size || 10000,
          status: status || (imageId ? ('done' as const) : ('error' as const)),
          originFileObj,
          error,
          imageId,
        };
      },
    ),
  );

  const [getStickerUrl] = useMustacheSticker2Url(platform || 'SHOPEE', toUpperCase(region));

  useEffect(() => {
    if (fileType !== 'sticker') {
      return;
    }
    if (!platform) {
      return;
    }

    let hasTransed = false;

    Promise.all(
      fileList.map((v) => {
        return new Promise<UploadFile<ImageUploadResponse>>((resolve) => {
          if (!v.url?.includes('@')) {
            return resolve(v);
          }
          getStickerUrl(v.url).then((url) => {
            if (!url) {
              return v;
            }
            const v2 = { ...v };
            v2.url = url;
            v2.thumbUrl = url;
            hasTransed = true;
            return resolve(v2);
          });
        });
      }),
    ).then((list) => {
      if (hasTransed) {
        setFileList(list);
      }
    });
  }, [fileType, fileList, platform, getStickerUrl]);

  const [previewModalVisible, { setLeft: closePreview, setRight: openPreview }] = useToggle();
  const [previewImgUrl, setPreviewImgUrl] = useState<string | undefined>();

  const showUploadButton = useMemo(() => {
    if (!disabled && maxCount && maxCount <= fileList.length) {
      return false;
    }
    return true;
  }, [fileList.length, maxCount, disabled]);

  const isAllImageResolve = useMemo(
    () => !fileList.map((file) => file.status).includes('uploading'),
    [fileList],
  );

  useEffect(() => {
    uploadStatusNotify?.(isAllImageResolve);
  }, [isAllImageResolve, uploadStatusNotify]);

  const handleChange = useCallback(
    (uploadChangeParam: UploadChangeParam<UploadFile<ImageUploadResponse>>) => {
      if (!disabled) {
        const { fileList, file } = uploadChangeParam;

        if (file.status === 'error') {
          message.error(t('ViewsChat.上传失败'));
        }

        if (fileType === 'sticker') {
          const {
            current: { onImagesChange },
          } = refs;

          onImagesChange(
            fileList
              .filter((v) => v.status !== 'error')
              .map((v) => {
                return {
                  // @ts-ignore
                  imageId: v.imageId,
                };
              }),
          );
        }

        setFileList(fileList.filter((v) => v.status !== 'error'));
      }
    },
    [disabled, fileType, t],
  );

  const handlePreview = useCallback(
    (file: UploadFile<ImageUploadResponse>) => {
      if (file.response) {
        setPreviewImgUrl(file.response?.data.localUrl || file.response?.data.url);
        openPreview();
      } else if (file.url) {
        setPreviewImgUrl(file.url);
        openPreview();
      } else {
        message.error(t('upload.previewFailed'));
      }
    },
    [openPreview, t],
  );

  const refs = useRef({
    onImagesChange,
  });
  refs.current = {
    onImagesChange,
  };

  useEffect(() => {
    const {
      current: { onImagesChange },
    } = refs;

    if (isAllImageResolve && platform) {
      onImagesChange(
        fileList.map((file, index) => {
          const { response, ...others } = file as UploadFile<ImageUploadResponse>;
          if ((response?.data?.url || response?.data?.localUrl) && response.data.id) {
            uploadNotify({
              resourceId: response.data.id,
              ossUrl: response?.data?.localUrl || response?.data?.url,
              platform,
            }).catch(noop);
          }

          return {
            index,
            imageId:
              (isQa ? response?.data?.platformUrl : response?.data?.localUrl) ||
              response?.data?.url,
            resourceId: response?.data?.id,
            ossUrl: response?.data?.localUrl || response?.data?.url,
            platformUrl: response?.data?.platformUrl,
            durationSeconds: response?.data?.durationSeconds,
            platform,
            ...others,
            thumbUrl: response?.data?.thumbUrl,
          };
        }),
      );
    }

    if (isAllImageResolve && !platform) {
      onImagesChange(
        fileList.map((file) => {
          const { response, name, fileName = name } = file;

          const { data } = (response as any) || {};

          const { resourceCentreId = '', url = '' } = data || {};

          return {
            imageId: url,
            media: JSON.stringify({
              mediaId: resourceCentreId,
              label: fileName,
              localUrl: url,
              coverUrl: url,
              durationSeconds: 0,
              mediaType: fileType === 'video' ? 'MEDIA_VIDEO' : 'MEDIA_IMAGE',
            }),
            url,
            ...file,
          };
        }),
      );
    }
  }, [fileList, isAllImageResolve, platform, isQa, fileType]);

  let upLoadBtnText = t('ViewsChat.上传视频');
  if (fileType === 'image') {
    upLoadBtnText = t('upload.upload');
  } else if (fileType === 'sticker') {
    upLoadBtnText = t('upload.添加表情');
  }

  const imageSizeLimit = (() => {
    if (platform) {
      return platformUploadSizeLimitMap[toLowerCase(platform)];
    }

    if (isAeProduct) {
      return platformUploadSizeLimitMap.aliexpress;
    }

    const { aliexpress, ...others } = platformUploadSizeLimitMap;

    return Math.min(...Object.values(others));
  })();

  const imageAccept = (() => {
    if (platform) {
      return platformUploadImageAcceptMap[toLowerCase(platform)];
    }

    if (isAeProduct) {
      return platformUploadImageAcceptMap.aliexpress;
    }

    const { aliexpress, ...others } = platformUploadImageAcceptMap;

    return uniq(Object.values(others).flat());
  })();

  let upLoadTipText = t('upload.视频提示', {
    num: 10,
    size: platform === 'LAZADA' || isTiKTokUpperCase(platform) ? 100 : 30,
    type: 'mp4',
    time: `${platform === 'LAZADA' ? '0s~180s' : '10s~60s'}`,
  });
  if (fileType === 'image') {
    upLoadTipText = t('upload.limit', {
      num: 10,
      size: (imageSizeLimit / 1024 / 1024).toFixed(0),
      type: imageAccept.join(','),
    });
  } else if (fileType === 'sticker') {
    upLoadTipText = t('upload.表情提示', { num: 3 });
  } else if (fileType === 'qrcode') {
    upLoadTipText = '此处二维码仅供预览，实际下发时会获取订单关联的二维码';
  }

  return (
    <div className={className} style={style}>
      <Upload
        className={styles.container}
        showUploadList={
          fileType === 'follow' || fileType === 'qrcode' ? { showRemoveIcon: false } : true
        }
        accept={fileType === 'image' ? imageAccept.map((_) => `.${_}`).join(',') : '.mp4'}
        name="file"
        headers={{
          [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
        }}
        listType="picture-card"
        fileList={fileList}
        iconRender={
          fileType === 'video'
            ? (file) => (
                <ProxyVideo src={file.url}>
                  <track kind="captions" />
                </ProxyVideo>
              )
            : (file) => <img alt="" src={file.url} />
        }
        beforeUpload={useCallback(
          async (file) => {
            const canUpload = () => {
              uploadStatusNotify?.(false);
              return Promise.resolve();
            };
            if (fileType === 'video') {
              if (!file.type?.includes('mp4')) {
                return Upload.LIST_IGNORE;
              }
              const { errorText } = await validatorVideo(platform, file);
              if (errorText) {
                message.warn(errorText);
                return Upload.LIST_IGNORE;
              }
            } else if (fileType === 'image') {
              // if (!file.type?.startsWith('video')) {
              if (file.size > imageSizeLimit) {
                message.warn(
                  t('upload.limitWarn', { size: `${(imageSizeLimit / 1024 / 1024).toFixed(2)}M` }),
                );
                return Upload.LIST_IGNORE;
              } else if (platform === 'LAZADA' || !platform) {
                return validatorLazadaImage(file).then((res): any => {
                  if (res) {
                    message.warn(t(res));
                    return Upload.LIST_IGNORE;
                  }
                  return canUpload();
                });
              }
            }
            return canUpload();
          },
          [t, imageSizeLimit, platform, fileType, uploadStatusNotify],
        )}
        previewFile={(file) => {
          if (file.type.includes('image')) {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (reader.result) {
                  resolve(reader.result as string);
                }
              };
              reader.readAsDataURL(file);
            });
          }

          const videoUrl = URL.createObjectURL(file);
          return getVideoThumbUrl(videoUrl)
            .catch(() => '')
            .then((thumbUrl) => {
              return thumbUrl;
            })
            .finally(() => {
              URL.revokeObjectURL(videoUrl);
            });
        }}
        onPreview={handlePreview}
        onChange={handleChange}
        openFileDialogOnClick={fileType !== 'sticker'}
        {...otherProps}
      >
        {addable && showUploadButton && fileType !== 'follow' && fileType !== 'qrcode' && (
          <div>
            {fileType === 'sticker' && platform ? (
              <ToolbarStickerPopover
                region={region!}
                platform={platform}
                onChange={(sticker) => {
                  const {
                    current: { onImagesChange },
                  } = refs;
                  const imageId = getStickerImageId(sticker.raw.pid, sticker.raw.sid);

                  onImagesChange([
                    ...fileSource.map(({ imageId, pid, sid }) => {
                      return {
                        imageId,
                        pid,
                        sid,
                      };
                    }),
                    {
                      imageId,
                      pid: sticker.raw.pid,
                      sid: sticker.raw.sid,
                    },
                  ]);

                  setFileList([
                    ...fileList,
                    {
                      type: fileType,
                      uid: `${imageId}#${fileList.length}`,
                      url: imageId,
                      thumbUrl: imageId,
                      status: 'done',
                      // @ts-ignore
                      imageId,
                      pid: sticker.raw.pid,
                      sid: sticker.raw.sid,
                    },
                  ]);
                }}
              >
                <div>
                  <PlusOutlined />
                  <div className={styles.uploadButton}>{upLoadBtnText}</div>
                </div>
              </ToolbarStickerPopover>
            ) : (
              <>
                <PlusOutlined />
                <div className={styles.uploadButton}>{upLoadBtnText}</div>
              </>
            )}
          </div>
        )}
      </Upload>
      {fileType !== 'follow' ? (
        <div className={styles.imageTip}>
          <AlertOutlined />
          <span className={styles.redText}>{t('upload.attentions')}</span>
          {upLoadTipText}
        </div>
      ) : null}
      <Modal visible={previewModalVisible} footer={null} onCancel={closePreview}>
        {fileType !== 'video' ? (
          <img alt="example" style={{ width: '100%' }} src={previewImgUrl} />
        ) : (
          <ProxyVideo controls src={previewImgUrl} style={{ width: '100%' }}>
            <track kind="captions" />
          </ProxyVideo>
        )}
      </Modal>
    </div>
  );
};

export default FileUpload;
